import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useFetch } from "@/lib/hooks";
import { useWorkspace } from "@/context/WorkspaceContext";
import { errorMessage } from "@/lib/api";
import { gbp } from "./Credit";

const PLAN_FIELDS = [["name", "Plan name", "text"], ["monthly_fee_gbp", "Monthly fee (£)", "number"], ["included_credit_gbp", "Credit included each month (£)", "number"],
  ["markup_pct", "Markup on real cost (%)", "number"]];
const FIELDS = [["trial_days", "Free trial (days)", "number"], ["trial_credit_gbp", "Trial credit (£)", "number"]];

function PlanFields({ p, i, onChange }) {
  return (
    <div className="rounded-lg border p-4" data-testid={`owner-plan-${p.id}`}>
      <p className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">Plan {i + 1} · {gbp(0.12 * (1 + Number(p.markup_pct) / 100))} charged for a £0.12 search</p>
      <div className="grid gap-3 sm:grid-cols-2">
        {PLAN_FIELDS.map(([k, l, t]) => <div key={k} className="space-y-1.5"><Label>{l}</Label><Input type={t} value={p[k]} onChange={(e) => onChange(i, k, e.target.value)} data-testid={`owner-plan-${p.id}-${k}`} /></div>)}
      </div>
    </div>
  );
}

function PricingForm({ onSaved }) {
  const { api } = useWorkspace();
  const { data } = useFetch("/platform/billing-settings");
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (data) setF({ ...data, topup_packs_gbp: data.topup_packs_gbp.join(", ") }); }, [data]);
  if (!f) return <Loader2 className="h-5 w-5 animate-spin" />;
  const setPlan = (i, k, v) => setF({ ...f, plans: f.plans.map((p, j) => (j === i ? { ...p, [k]: v } : p)) });
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...f, topup_packs_gbp: f.topup_packs_gbp.split(",").map((x) => Number(x.trim())).filter(Boolean),
        plans: f.plans.map((p) => ({ ...p, monthly_fee_gbp: Number(p.monthly_fee_gbp), included_credit_gbp: Number(p.included_credit_gbp), markup_pct: Number(p.markup_pct) })) };
      FIELDS.forEach(([k, , t]) => { if (t === "number") body[k] = Number(body[k]); });
      await api.put("/platform/billing-settings", body); toast.success("Pricing saved — applies to all organisations"); onSaved();
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="panel space-y-4 p-6" data-testid="owner-pricing">
      <p className="font-medium">Pricing (all organisations)</p>
      <div className="grid gap-3 lg:grid-cols-2">{f.plans.map((p, i) => <PlanFields key={p.id} p={p} i={i} onChange={setPlan} />)}</div>
      <div className="grid gap-3 sm:grid-cols-3">
        {FIELDS.map(([k, l, t]) => <div key={k} className="space-y-1.5"><Label>{l}</Label><Input type={t} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`owner-pricing-${k}`} /></div>)}
        <div className="space-y-1.5"><Label>Top-up packs (£, comma separated)</Label><Input value={f.topup_packs_gbp} onChange={(e) => setF({ ...f, topup_packs_gbp: e.target.value })} data-testid="owner-pricing-topup_packs_gbp" /></div>
      </div>
      <p className="text-xs text-muted-foreground" data-testid="owner-pricing-example">Trials use the first plan's markup. Changing a monthly fee applies to new subscriptions and plan switches; existing subscribers keep their current price.</p>
      <Button onClick={save} disabled={busy} data-testid="owner-pricing-save">{busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}Save pricing</Button>
    </div>
  );
}

function OrgRow({ o, act }) {
  return (
    <tr className="border-b border-border/60" data-testid={`owner-org-${o.id}`}>
      <td className="p-3"><p className="font-medium">{o.name}</p><p className="text-xs text-muted-foreground">{o.billing_exempt ? "Not billed" : o.status}{!o.billing_exempt && ` · balance ${gbp(o.balance_gbp)}`}</p></td>
      <td className="text-right font-mono">{gbp(o.received_gbp)}</td><td className="text-right font-mono">{gbp(o.real_cost_gbp)}</td>
      <td className="text-right font-mono">{gbp(o.usage_charged_gbp)}</td>
      <td className={`text-right font-mono ${o.profit_gbp < 0 ? "text-destructive" : "text-emerald-600 dark:text-emerald-400"}`} data-testid={`owner-org-profit-${o.id}`}>{gbp(o.profit_gbp)}</td>
      <td className="p-3 text-right"><div className="flex justify-end gap-1">
        <Button size="sm" variant="ghost" onClick={() => act(o, "credit")} data-testid={`owner-org-credit-${o.id}`}>Give credit</Button>
        <Button size="sm" variant="ghost" onClick={() => act(o, "trial")} data-testid={`owner-org-trial-${o.id}`}>+7 days trial</Button>
        <Button size="sm" variant="ghost" onClick={() => act(o, "exempt")} data-testid={`owner-org-exempt-${o.id}`}>{o.billing_exempt ? "Start billing" : "Don't bill"}</Button>
      </div></td>
    </tr>
  );
}

export function OwnerBilling() {
  const { api } = useWorkspace();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const { data, reload } = useFetch("/platform/billing-overview", { month });
  const act = async (o, what) => {
    try {
      if (what === "credit") {
        const v = window.prompt(`Credit to add for ${o.name} (£, negative to remove):`, "10");
        if (!v || Number.isNaN(Number(v))) return;
        await api.post(`/platform/organisations/${o.id}/credit`, { amount_gbp: Number(v), note: "Added by platform owner" });
      } else {
        await api.post(`/platform/organisations/${o.id}/billing`, what === "exempt" ? { billing_exempt: !o.billing_exempt } : { billing_exempt: o.billing_exempt, extend_trial_days: 7 });
      }
      toast.success("Updated"); reload();
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const t = data?.totals;
  return (
    <div className="space-y-5" data-testid="owner-billing">
      <PricingForm onSaved={reload} />
      <div className="panel overflow-x-auto p-0">
        <div className="flex flex-wrap items-center gap-3 border-b p-4">
          <p className="font-medium">Revenue and profit</p>
          <Input type="month" className="h-9 w-40" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="owner-billing-month" />
          {t && <p className="text-sm" data-testid="owner-billing-totals">Received <b>{gbp(t.received_gbp)}</b> · real cost <b>{gbp(t.real_cost_gbp)}</b> · profit <b className={t.profit_gbp < 0 ? "text-destructive" : ""}>{gbp(t.profit_gbp)}</b></p>}
        </div>
        <table className="w-full text-sm"><thead><tr className="border-b text-left text-xs uppercase text-muted-foreground">
          <th className="p-3">Organisation</th><th className="text-right">Received</th><th className="text-right">Real cost</th><th className="text-right">Credit used</th><th className="text-right">Profit</th><th /></tr></thead>
          <tbody>{(data?.organisations || []).map((o) => <OrgRow key={o.id} o={o} act={act} />)}</tbody></table>
        <p className="p-4 text-xs text-muted-foreground">Received = subscriptions + top-ups paid in the month. Profit = received − real provider cost, before Stripe fees and VAT. Credit used is usage at your marked-up price.</p>
      </div>
    </div>
  );
}
